/**
 * PerformanceCards Component
 *
 * Displays three horizontal cards showing AI portfolio performance.
 * Each card shows AI logo, name, and all-time return percentage.
 * Shared between IdeasPage teaser and full AIArenaPage.
 */

import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { colors, spacing, fontStyles, calculateChangeColor } from '../../styles';
import {
  getInvestingArenaPortfolios,
  InvestingArenaPortfolio,
  AIModelId,
  AI_MODEL_NAMES,
  AI_MODEL_COLORS,
  AI_MODEL_ICONS,
} from '../../apis/investingArena';

// Preferred display order
const AI_MODEL_ORDER: AIModelId[] = ['openai', 'gemini', 'claude'];

const skeletonPulse = css`
  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  animation: pulse 1.5s ease-in-out infinite;
`;

// Skeleton loader for the cards
export const PerformanceCardsSkeleton: FC = () => {
  return (
    <div
      css={css`
        display: flex;
        gap: ${spacing.centi};
      `}
    >
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          css={css`
            flex: 1;
            min-width: 0;
            padding: ${spacing.deci};
            border-radius: 12px;
            background: var(--card-background);
            ${skeletonPulse}
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.centi};
              margin-bottom: ${spacing.centi};
            `}
          >
            <div
              css={css`
                width: 24px;
                height: 24px;
                border-radius: 50%;
                background: ${colors.cloud};
                html[data-theme='dark'] & {
                  background: ${colors.tungsten};
                }
              `}
            />
            <div
              css={css`
                width: 60px;
                height: 14px;
                border-radius: 4px;
                background: ${colors.cloud};
                html[data-theme='dark'] & {
                  background: ${colors.tungsten};
                }
              `}
            />
          </div>
          <div
            css={css`
              width: 50px;
              height: 20px;
              border-radius: 4px;
              background: ${colors.cloud};
              html[data-theme='dark'] & {
                background: ${colors.tungsten};
              }
            `}
          />
        </div>
      ))}
    </div>
  );
};

interface PerformanceCardProps {
  portfolio: InvestingArenaPortfolio;
  daysSinceStart: number;
  onClick?: () => void;
}

const PerformanceCard: FC<PerformanceCardProps> = ({ portfolio, daysSinceStart, onClick }) => {
  // API returns return_pct in decimal form (0.305 = 30.5%)
  const returnPct = portfolio.return_pct * 100;
  const isPositive = returnPct >= 0;
  // Use gradient color based on magnitude (matches ColoredPercentChange)
  const returnColor = calculateChangeColor(portfolio.return_pct);
  const modelIcon = AI_MODEL_ICONS[portfolio.id];
  const modelColor = AI_MODEL_COLORS[portfolio.id];

  return (
    <button
      onClick={onClick}
      css={css`
        flex: 1;
        min-width: 0;
        padding: ${spacing.deci};
        border-radius: 8px;
        background: ${colors.snow};
        border: 0.5px solid ${colors.cloud};
        cursor: pointer;
        text-align: left;
        transition: transform 0.15s ease;

        html[data-theme='dark'] & {
          background: var(--background-color);
          border-color: ${colors.tungsten};
        }

        &:hover {
          transform: translateY(-2px);
        }

        &:active {
          transform: translateY(0);
        }
      `}
    >
      {/* AI Model Header */}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.milli};
          margin-bottom: ${spacing.centi};
        `}
      >
        <div
          css={css`
            width: 24px;
            height: 24px;
            flex-shrink: 0;
            border-radius: 50%;
            background: white;
            border: 1px solid ${colors.cloud};
            display: flex;
            align-items: center;
            justify-content: center;

            html[data-theme='dark'] & {
              background: #2a2a2a;
              border-color: ${colors.tungsten};
            }
          `}
        >
          {modelIcon && (
            <img
              src={modelIcon}
              alt=""
              css={css`
                width: 14px;
                height: 14px;

                html[data-theme='dark'] & {
                  filter: ${portfolio.id === 'openai' ? 'invert(1)' : 'none'};
                }
              `}
            />
          )}
        </div>
        <span
          css={css`
            ${fontStyles.flea}
            color: ${modelColor};
            font-weight: 500;
            white-space: nowrap;

            html[data-theme='dark'] & {
              color: ${portfolio.id === 'openai' ? colors.snow : modelColor};
            }
          `}
        >
          {AI_MODEL_NAMES[portfolio.id]}
        </span>
      </div>

      {/* Return Percentage */}
      <div>
        <span
          css={css`
            ${fontStyles.lion}
            color: ${returnColor};
            font-weight: 600;
            white-space: nowrap;
          `}
        >
          {isPositive ? '+' : '-'}
          {Math.abs(returnPct).toFixed(1)}%
        </span>
        <br />
        <span
          css={css`
            ${fontStyles.flea}
            color: ${colors.silver};
            font-weight: 500;
          `}
        >
          {daysSinceStart}d
        </span>
      </div>
    </button>
  );
};

interface PerformanceCardsProps {
  onCardClick?: (portfolio: InvestingArenaPortfolio) => void;
}

export const PerformanceCards: FC<PerformanceCardsProps> = ({ onCardClick }) => {
  const {
    data: portfolios,
    isLoading,
    isError,
  } = useQuery(['investing-arena', 'portfolios'], getInvestingArenaPortfolios, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 0, // function throws on error; don't retry to avoid multi-second delay on failure
  });

  // Sort portfolios by preferred display order
  const sortedPortfolios = useMemo(() => {
    if (!portfolios) return [];
    return [...portfolios].sort((a, b) => {
      const aIndex = AI_MODEL_ORDER.indexOf(a.id);
      const bIndex = AI_MODEL_ORDER.indexOf(b.id);
      return aIndex - bIndex;
    });
  }, [portfolios]);

  // Calculate days since AI Arena launch (November 18, 2025)
  const daysSinceStart = useMemo(() => {
    const launchDate = new Date(2025, 10, 18); // Nov 18, 2025
    return Math.floor((Date.now() - launchDate.getTime()) / (1000 * 60 * 60 * 24));
  }, []);

  if (isLoading) {
    return <PerformanceCardsSkeleton />;
  }

  if (isError || !sortedPortfolios || sortedPortfolios.length === 0) {
    return null;
  }

  return (
    <div
      css={css`
        display: flex;
        gap: ${spacing.centi};
      `}
    >
      {sortedPortfolios.map((portfolio) => (
        <PerformanceCard
          key={portfolio.id}
          portfolio={portfolio}
          daysSinceStart={daysSinceStart}
          onClick={onCardClick ? () => onCardClick(portfolio) : undefined}
        />
      ))}
    </div>
  );
};

export default PerformanceCards;
